import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AppShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSpeechRecognition, useAudioRecorder, VoiceTextarea } from "@/lib/speech";
import { Mic, MicOff, Square, Play, Trash2, Zap, Clock, MapPin, User, Plus, ArrowRight, AlertCircle, Merge, Check, Camera, X, FileText } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 focus:border-red-500 focus:outline-none";

const CATEGORIES_QUICK = ["Discipline","Warning letter","Suspension","Discharge or termination","Supervisor performing bargaining-unit work","Overtime bypass","Pay shortage","Safety issue","Harassment or intimidation","Other contract violation"];
const QUICK_TAGS = ["Warning Letter","Suspension","Supervisor Working","Overtime","Harassment","Safety","Attendance","Payroll","Other"];

/** Voice-to-text button — appends transcribed speech into a textarea. */
export function SpeakInsteadButton({ value, onChange, testid="speak-btn" }) {
  const { supported, isListening, interim, error, start, stop } = useSpeechRecognition();
  const [buffer, setBuffer] = React.useState("");
  React.useEffect(() => { setBuffer(value || ""); }, [value]);

  const handleFinal = (finalText) => {
    const next = (buffer ? buffer + " " : "") + finalText;
    setBuffer(next);
    onChange(next);
  };

  if (!supported) return null;
  return (
    <div className="mt-1 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {!isListening ? (
          <Button type="button" size="sm" variant="outline" onClick={()=>start(handleFinal)} data-testid={testid}>
            <Mic className="h-4 w-4 mr-1"/>Speak Instead
          </Button>
        ) : (
          <Button type="button" size="sm" onClick={stop} className="bg-red-600 hover:bg-red-700 text-white animate-pulse" data-testid={`${testid}-stop`}>
            <MicOff className="h-4 w-4 mr-1"/>Stop
          </Button>
        )}
        {isListening && <span className="text-xs text-red-600">Listening... speak clearly</span>}
        {error && <span className="text-xs text-amber-700">{error}</span>}
      </div>
      {interim && <p className="text-xs text-slate-500 italic">{interim}</p>}
    </div>
  );
}

/** Audio recorder — records blob, allows preview, provides blob to caller for upload. */
export function AudioRecorder({ onRecorded, testid="audio-rec" }) {
  const { isRecording, blob, duration, error, start, stop, reset } = useAudioRecorder();
  const audioUrl = React.useMemo(() => blob ? URL.createObjectURL(blob) : null, [blob]);
  React.useEffect(() => { if (blob && onRecorded) onRecorded(blob); }, [blob]);
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
      <div className="flex items-center gap-2">
        {!isRecording && !blob && (
          <Button type="button" size="sm" onClick={start} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`${testid}-start`}>
            <Mic className="h-4 w-4 mr-1"/>Record audio note
          </Button>
        )}
        {isRecording && (
          <Button type="button" size="sm" onClick={stop} className="bg-red-600 hover:bg-red-700 text-white animate-pulse" data-testid={`${testid}-stop`}>
            <Square className="h-4 w-4 mr-1"/>Stop ({duration}s)
          </Button>
        )}
        {blob && !isRecording && (
          <>
            <audio controls src={audioUrl} className="h-8"/>
            <Button type="button" size="sm" variant="outline" onClick={reset}><Trash2 className="h-4 w-4"/></Button>
          </>
        )}
      </div>
      {error && <p className="text-xs text-amber-700">{error}</p>}
      <p className="text-xs text-slate-500">Audio note is optional. It stays private to your account.</p>
    </div>
  );
}

/** Big "I Just Left the Office" quick-capture card + panel (used on Dashboard). */
export function IncidentQuickCapture({ onCreated, compact = false }) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({title:"", incident_date:new Date().toISOString().slice(0,10), incident_time:"", work_location:"", supervisor:"", what_happened:"", who_involved:"", likely_category:"", urgency:"Medium"});
  const [audioBlob, setAudioBlob] = React.useState(null);
  const [photoFiles, setPhotoFiles] = React.useState([]); // File[] queued for upload
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const photoInputRef = React.useRef(null);

  // Attempt to grab location once modal opens
  React.useEffect(() => {
    if (!open || f.work_location) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`;
        setF(prev => prev.work_location ? prev : ({...prev, work_location: `Coords: ${coords}`}));
      },
      () => {},
      { timeout: 4000, maximumAge: 60000 }
    );
  }, [open]);

  const toggleTag = (t) => setSelectedTags(s => s.includes(t) ? s.filter(x=>x!==t) : [...s, t]);

  const onPickPhotos = (e) => {
    const picked = Array.from(e.target.files || []);
    const merged = [...photoFiles, ...picked].slice(0, 8); // cap at 8 per capture
    setPhotoFiles(merged);
    e.target.value = ""; // allow re-selecting the same file later
  };
  const removePhoto = (idx) => setPhotoFiles(prev => prev.filter((_, i) => i !== idx));

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim() && !f.what_happened.trim() && !audioBlob) {
      toast.error("Please add a quick title or describe what happened before saving.");
      return;
    }
    setBusy(true);
    try {
      const payload = {...f};
      if (selectedTags.length > 0) {
        payload.notes = "Tags: " + selectedTags.join(", ");
      }
      const { data } = await api.post("/incidents", payload);
      if (audioBlob) {
        const fd = new FormData();
        fd.append("file", audioBlob, "note.webm");
        try { await api.post(`/incidents/${data.id}/audio`, fd, {headers:{"Content-Type":"multipart/form-data"}}); }
        catch { toast.warning("Incident saved. Audio upload failed."); }
      }
      if (photoFiles.length > 0) {
        let uploaded = 0, failed = 0;
        for (const pf of photoFiles) {
          const fd = new FormData();
          fd.append("file", pf);
          try {
            await api.post(`/incidents/${data.id}/photos`, fd, {headers:{"Content-Type":"multipart/form-data"}});
            uploaded += 1;
          } catch { failed += 1; }
        }
        if (failed > 0) toast.warning(`Incident saved. ${uploaded} photo(s) uploaded, ${failed} failed.`);
      }
      toast.success("Incident recorded.");
      setOpen(false);
      setF({title:"", incident_date:new Date().toISOString().slice(0,10), incident_time:"", work_location:"", supervisor:"", what_happened:"", who_involved:"", likely_category:"", urgency:"Medium"});
      setAudioBlob(null); setSelectedTags([]); setPhotoFiles([]);
      onCreated && onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      {compact ? (
        <Button onClick={()=>setOpen(true)} data-testid="quick-capture-btn" className="bg-red-600 hover:bg-red-700 text-white">
          <Zap className="h-4 w-4 mr-1"/>Capture Incident
        </Button>
      ) : (
      <button onClick={()=>setOpen(true)} data-testid="quick-capture-btn"
        className="w-full text-left bg-white border border-slate-200 rounded-md p-6 shadow-sm transition-lift hover:shadow-md hover:border-slate-300">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
            <Zap className="h-6 w-6 text-red-600"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black font-display text-2xl text-slate-900">I Just Left the Office</div>
            <div className="mt-1 text-slate-600 text-sm">Something happened today? Capture everything while it's still fresh.</div>
            <div className="mt-4">
              <span className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold rounded-md px-5 py-2.5 text-sm shadow-sm hover:bg-red-700 transition-colors">
                <Zap className="h-4 w-4"/>Start Recording
              </span>
            </div>
            <div className="mt-5 text-xs text-slate-500">
              <span>Examples:</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {["Supervisor meeting","Warning letter","Harassment","Overtime violation","Safety concern","Discipline"].map(x=>(
                  <span key={x} className="bg-white text-slate-600 border border-slate-300 rounded-full px-2.5 py-0.5">{x}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </button>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={()=>setOpen(false)}>
          <form onClick={e=>e.stopPropagation()} onSubmit={save} data-testid="quick-capture-form"
            className="bg-white rounded-md max-w-2xl w-full my-8 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-black font-display text-slate-900">Record Incident</h2>
              <button type="button" onClick={()=>setOpen(false)} className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <p className="text-sm text-slate-600">Capture the facts fast. You can convert this to a grievance later.</p>

            <label className="block"><span className="text-sm font-medium text-slate-700">Quick title</span>
              <input className={inputCls} value={f.title} onChange={e=>setF({...f, title:e.target.value})} placeholder="e.g., Supervisor loaded my truck" data-testid="qc-title"/>
            </label>

            <div>
              <span className="text-sm font-medium text-slate-700 block mb-2">Quick tags <span className="text-slate-500 font-normal">(select all that apply)</span></span>
              <div className="flex flex-wrap gap-2" data-testid="qc-tags">
                {QUICK_TAGS.map(t => {
                  const sel = selectedTags.includes(t);
                  return <button type="button" key={t} onClick={()=>toggleTag(t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-lift ${sel?"bg-red-600 text-white border-red-600":"bg-white text-slate-700 border-slate-300 hover:border-red-300"}`}
                    data-testid={`qc-tag-${t.replace(/\s+/g,'-')}`}>
                    {sel && <Check className="h-3 w-3 inline mr-1"/>}{t}
                  </button>;
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block"><span className="text-sm font-medium text-slate-700 flex items-center gap-1"><Clock className="h-3.5 w-3.5"/>Date</span>
                <input type="date" className={inputCls} value={f.incident_date} onChange={e=>setF({...f, incident_date:e.target.value})} data-testid="qc-date"/>
                <span className="text-xs text-slate-500 mt-1 block">Auto-stamped now</span>
              </label>
              <label className="block"><span className="text-sm font-medium text-slate-700">Time</span>
                <input type="time" className={inputCls} value={f.incident_time} onChange={e=>setF({...f, incident_time:e.target.value})}/>
              </label>
              <label className="block"><span className="text-sm font-medium text-slate-700">Urgency</span>
                <select className={inputCls} value={f.urgency} onChange={e=>setF({...f, urgency:e.target.value})}>
                  <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block"><span className="text-sm font-medium text-slate-700 flex items-center gap-1"><MapPin className="h-3.5 w-3.5"/>Where</span>
                <input className={inputCls} value={f.work_location} onChange={e=>setF({...f, work_location:e.target.value})} placeholder="Hub, route, building — location auto-detected if allowed" data-testid="qc-where"/>
              </label>
              <label className="block"><span className="text-sm font-medium text-slate-700 flex items-center gap-1"><User className="h-3.5 w-3.5"/>Supervisor / manager</span>
                <input className={inputCls} value={f.supervisor} onChange={e=>setF({...f, supervisor:e.target.value})} data-testid="qc-supervisor"/>
              </label>
            </div>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">What happened?</span>
              <VoiceTextarea
                testid="qc-speak"
                rows={4}
                className={inputCls}
                value={f.what_happened}
                onChange={v=>setF(prev=>({...prev, what_happened:v}))}
                placeholder="Describe what happened in plain language..."
                ariaLabel="What happened"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Who was involved / witnesses?</span>
              <textarea rows={2} className={inputCls} value={f.who_involved} onChange={e=>setF({...f, who_involved:e.target.value})} placeholder="Names, roles, witnesses..." data-testid="qc-who"/>
            </label>
            <label className="block"><span className="text-sm font-medium text-slate-700">Likely category</span>
              <select className={inputCls} value={f.likely_category} onChange={e=>setF({...f, likely_category:e.target.value})} data-testid="qc-category">
                <option value="">— unsure —</option>
                {CATEGORIES_QUICK.map(c=><option key={c}>{c}</option>)}
              </select>
            </label>
            <div>
              <span className="text-sm font-medium text-slate-700 block mb-1">Optional audio note</span>
              <AudioRecorder onRecorded={setAudioBlob} testid="qc-audio"/>
            </div>

            <div>
              <span className="text-sm font-medium text-slate-700 block mb-1">Photos &amp; documents <span className="text-slate-500 font-normal">(warning letters, screenshots, damage — up to 8)</span></span>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={onPickPhotos}
                className="hidden"
                data-testid="qc-photo-input"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={()=>photoInputRef.current?.click()} data-testid="qc-photo-pick">
                  <Camera className="h-4 w-4 mr-1"/>Add photo or PDF
                </Button>
                {photoFiles.length > 0 && (
                  <span className="text-xs text-slate-500" data-testid="qc-photo-count">{photoFiles.length} attached</span>
                )}
              </div>
              {photoFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="qc-photo-previews">
                  {photoFiles.map((pf, idx) => {
                    const isImg = pf.type.startsWith("image/");
                    const url = isImg ? URL.createObjectURL(pf) : null;
                    return (
                      <div key={idx} className="relative border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid={`qc-photo-${idx}`}>
                        {isImg ? (
                          <img src={url} alt={pf.name} className="w-full h-20 object-cover"/>
                        ) : (
                          <div className="w-full h-20 flex items-center justify-center text-slate-500">
                            <FileText className="h-6 w-6"/>
                          </div>
                        )}
                        <div className="px-1.5 py-1 text-[10px] text-slate-600 truncate" title={pf.name}>{pf.name}</div>
                        <button
                          type="button"
                          onClick={()=>removePhoto(idx)}
                          className="absolute top-1 right-1 h-5 w-5 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-red-600"
                          data-testid={`qc-photo-remove-${idx}`}
                        >
                          <X className="h-3 w-3"/>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="qc-save">{busy?"Saving...":"Save Incident"}</Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/** Incident history page. */
export function IncidentsList() {
  const [items, setItems] = React.useState([]);
  const [selected, setSelected] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const nav = useNavigate();

  const load = async () => setItems((await api.get("/incidents")).data);
  React.useEffect(()=>{ load(); }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const convertSingle = async (id) => {
    if (!window.confirm("Convert this incident into a grievance? This starts a new case.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${id}/convert`, { incident_ids: [] });
      toast.success(`Grievance ${data.case_number} created`);
      nav(`/new-grievance/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info(e.response?.data?.detail || "Converting this incident into a grievance requires Premium. Taking you to Billing to see your options.");
        nav("/billing");
      } else {
        toast.error(e.response?.data?.detail || "Convert failed");
      }
    } finally { setBusy(false); }
  };

  const convertCombined = async () => {
    if (selected.length < 2) return toast.info("Select at least 2 incidents to combine.");
    if (!window.confirm(`Combine ${selected.length} incidents into ONE grievance?`)) return;
    setBusy(true);
    try {
      const primary = selected[0];
      const extras = selected.slice(1);
      const { data } = await api.post(`/incidents/${primary}/convert`, { incident_ids: extras });
      toast.success(`Grievance ${data.case_number} created from ${selected.length} incidents`);
      nav(`/new-grievance/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info(e.response?.data?.detail || "Converting this incident into a grievance requires Premium. Taking you to Billing to see your options.");
        nav("/billing");
      } else {
        toast.error(e.response?.data?.detail || "Convert failed");
      }
    } finally { setBusy(false); }
  };

  const [delId, setDelId] = React.useState(null);
  const del = async () => {
    const id = delId; setDelId(null);
    await api.delete(`/incidents/${id}`); toast.success("Deleted"); load();
  };

  const pending = items.filter(i => !i.converted_to_case_id);
  const converted = items.filter(i => i.converted_to_case_id);

  return (
    <AppShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black font-display text-slate-900">Incident History</h1>
          <p className="text-slate-600 text-sm mt-1">Quick records you captured. Convert to a grievance when you're ready.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <IncidentQuickCapture compact onCreated={()=>load()} />
          {selected.length >= 2 && (
            <Button onClick={convertCombined} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="combine-btn">
              <Merge className="h-4 w-4 mr-1"/>Combine {selected.length} into Grievance
            </Button>
          )}
          <Link to="/dashboard"><Button variant="outline">Back to Dashboard</Button></Link>
        </div>
      </div>

      <section className="mt-8">
        <h2 className="text-xl font-black font-display text-slate-900 mb-4">Pending — Not Yet Converted <span className="text-slate-400 text-sm font-normal">({pending.length})</span></h2>
        {pending.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-sm">No pending incidents. Record one from your dashboard.</div>
        ) : (
          <div className="space-y-3">
            {pending.map(i => (
              <div key={i.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`incident-row-${i.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input type="checkbox" checked={selected.includes(i.id)} onChange={()=>toggle(i.id)} className="mt-1" data-testid={`select-${i.id}`}/>
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500">{i.incident_date} {i.incident_time && `• ${i.incident_time}`} {i.urgency && `• ${i.urgency}`}</div>
                      <h3 className="font-bold text-slate-900 font-display">{i.title}</h3>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{i.what_happened || "—"}</p>
                      {i.likely_category && <span className="mt-2 inline-block text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-slate-700">{i.likely_category}</span>}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
                    <Link to={`/incidents/${i.id}`}><Button size="sm" variant="outline" data-testid={`view-${i.id}`}>View</Button></Link>
                    <Button size="sm" onClick={()=>convertSingle(i.id)} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`convert-${i.id}`}>
                      <ArrowRight className="h-3.5 w-3.5 mr-1"/>Convert
                    </Button>
                    <Button size="sm" variant="outline" onClick={()=>setDelId(i.id)} data-testid={`incident-delete-${i.id}`}><Trash2 className="h-3.5 w-3.5"/></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog open={!!delId} onOpenChange={(o)=>{ if(!o) setDelId(null); }} title="Delete this incident?" description="This permanently removes the incident record." confirmLabel="Delete" onConfirm={del} />

      {converted.length > 0 && (
        <section className="mt-8">
          <h2 className="text-xl font-black font-display text-slate-900 mb-4">Converted <span className="text-slate-400 text-sm font-normal">({converted.length})</span></h2>
          <div className="space-y-2">
            {converted.map(i => (
              <div key={i.id} className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm flex justify-between items-center" data-testid={`converted-${i.id}`}>
                <div>
                  <span className="text-xs text-slate-500">{i.incident_date}</span> — <span className="font-medium">{i.title}</span>
                </div>
                <Link to={`/cases/${i.converted_to_case_id}`}><Button size="sm" variant="outline">Open case</Button></Link>
              </div>
            ))}
          </div>
        </section>
      )}
    </AppShell>
  );
}

/** Timeline view for one incident. */
export function IncidentDetail() {
  const { id } = useParams();
  const [i, setI] = React.useState(null);
  const [photos, setPhotos] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const addPhotoRef = React.useRef(null);
  const nav = useNavigate();

  const loadPhotos = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/incidents/${id}/photos`);
      setPhotos(data || []);
    } catch { /* silent */ }
  }, [id]);

  React.useEffect(()=>{ api.get(`/incidents/${id}`).then(r=>setI(r.data)).catch(()=>toast.error("Not found")); }, [id]);
  React.useEffect(()=>{ if (i) loadPhotos(); }, [i, loadPhotos]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    let uploaded = 0, failed = 0;
    for (const pf of files) {
      const fd = new FormData();
      fd.append("file", pf);
      try {
        await api.post(`/incidents/${id}/photos`, fd, {headers:{"Content-Type":"multipart/form-data"}});
        uploaded += 1;
      } catch { failed += 1; }
    }
    setUploading(false);
    if (uploaded > 0) toast.success(`${uploaded} photo(s) uploaded`);
    if (failed > 0) toast.error(`${failed} photo(s) failed`);
    loadPhotos();
  };

  const [removeFid, setRemoveFid] = React.useState(null);
  const removePhoto = async () => {
    const fid = removeFid; setRemoveFid(null);
    try {
      await api.delete(`/incidents/${id}/photos/${fid}`);
      toast.success("Photo removed");
      loadPhotos();
    } catch { toast.error("Delete failed"); }
  };

  const photoUrl = (fid) => {
    const t = localStorage.getItem("fmg_token");
    return `${api.defaults.baseURL}/files/${fid}/download?auth=${encodeURIComponent(t)}`;
  };

  const convert = async () => {
    if (!window.confirm("Convert this incident into a grievance?")) return;
    try {
      const { data } = await api.post(`/incidents/${id}/convert`, { incident_ids: [] });
      toast.success(`Grievance ${data.case_number} created`);
      nav(`/new-grievance/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.info(e.response?.data?.detail || "Converting this incident into a grievance requires Premium. Taking you to Billing to see your options.");
        nav("/billing");
      } else {
        toast.error(e.response?.data?.detail || "Convert failed");
      }
    }
  };

  if (!i) return <AppShell><div>Loading...</div></AppShell>;

  return (
    <AppShell>
      <ConfirmDialog open={!!removeFid} onOpenChange={(o)=>{ if(!o) setRemoveFid(null); }} title="Remove this photo?" description="This permanently removes the photo from the incident." confirmLabel="Remove" onConfirm={removePhoto} />
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <div className="text-xs text-slate-500">Incident</div>
          <h1 className="text-2xl font-black font-display text-slate-900">{i.title}</h1>
          <div className="mt-1 text-sm text-slate-600">{i.incident_date} {i.incident_time && `• ${i.incident_time}`}{i.urgency && ` • ${i.urgency}`}</div>
        </div>
        <div className="flex gap-2">
          {!i.converted_to_case_id ? (
            <Button onClick={convert} className="bg-red-600 hover:bg-red-700 text-white" data-testid="detail-convert"><ArrowRight className="h-4 w-4 mr-1"/>Convert to Grievance</Button>
          ) : (
            <Link to={`/cases/${i.converted_to_case_id}`}><Button className="bg-slate-900 hover:bg-slate-800 text-white">Open case</Button></Link>
          )}
          <Link to="/incidents"><Button variant="outline">Back</Button></Link>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-slate-200 rounded-md p-6 space-y-4">
          <div><h3 className="font-bold text-slate-900 font-display">What happened</h3><p className="mt-1 text-sm whitespace-pre-wrap">{i.what_happened || "—"}</p></div>
          <div><h3 className="font-bold text-slate-900 font-display">Who was involved</h3><p className="mt-1 text-sm whitespace-pre-wrap">{i.who_involved || "—"}</p></div>
          <div><h3 className="font-bold text-slate-900 font-display">Where</h3><p className="mt-1 text-sm">{i.work_location || "—"}</p></div>
          <div><h3 className="font-bold text-slate-900 font-display">Supervisor / manager</h3><p className="mt-1 text-sm">{i.supervisor || "—"}</p></div>
          {i.likely_category && <div><h3 className="font-bold text-slate-900 font-display">Likely category</h3><p className="mt-1 text-sm">{i.likely_category}</p></div>}

          <div data-testid="incident-photos-section">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 font-display">Photos &amp; documents <span className="text-slate-500 font-normal text-sm">({photos.length})</span></h3>
              {!i.converted_to_case_id && (
                <>
                  <input ref={addPhotoRef} type="file" accept="image/*,application/pdf" multiple onChange={addPhotos} className="hidden" data-testid="detail-photo-input"/>
                  <Button size="sm" variant="outline" onClick={()=>addPhotoRef.current?.click()} disabled={uploading} data-testid="detail-photo-add">
                    <Camera className="h-4 w-4 mr-1"/>{uploading ? "Uploading..." : "Add"}
                  </Button>
                </>
              )}
            </div>
            {photos.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500 italic">No photos attached.</p>
            ) : (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="incident-photo-grid">
                {photos.map(p => {
                  const isImg = (p.content_type || "").startsWith("image/");
                  return (
                    <div key={p.id} className="relative border border-slate-200 rounded-md overflow-hidden bg-slate-50 group" data-testid={`incident-photo-${p.id}`}>
                      {isImg ? (
                        <a href={photoUrl(p.id)} target="_blank" rel="noreferrer">
                          <img src={photoUrl(p.id)} alt={p.original_filename} className="w-full h-28 object-cover"/>
                        </a>
                      ) : (
                        <a href={photoUrl(p.id)} target="_blank" rel="noreferrer" className="block w-full h-28 flex items-center justify-center text-slate-500">
                          <FileText className="h-8 w-8"/>
                        </a>
                      )}
                      <div className="px-2 py-1 text-[11px] text-slate-600 truncate" title={p.original_filename}>{p.original_filename}</div>
                      {!i.converted_to_case_id && (
                        <button
                          type="button"
                          onClick={()=>setRemoveFid(p.id)}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-slate-900/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          data-testid={`incident-photo-remove-${p.id}`}
                        >
                          <X className="h-3.5 w-3.5"/>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-bold text-slate-900">Timeline</h3>
          <ol className="mt-3 space-y-3 text-sm">
            <li className="flex gap-3"><span className="text-xs text-slate-500 w-20 flex-shrink-0">{i.created_at?.slice(0,10)}</span><span>Incident recorded</span></li>
            {i.updated_at !== i.created_at && <li className="flex gap-3"><span className="text-xs text-slate-500 w-20 flex-shrink-0">{i.updated_at?.slice(0,10)}</span><span>Last updated</span></li>}
            {i.converted_to_case_id && <li className="flex gap-3"><span className="text-xs text-slate-500 w-20 flex-shrink-0">→</span><span className="font-semibold text-red-600">Converted to grievance</span></li>}
          </ol>
        </div>
      </div>
    </AppShell>
  );
}
